import Skeleton from '@material-ui/lab/Skeleton'
import React, { useState } from 'react'
import { Dialog, Link, List, ListItem, ListItemIcon, ListItemText, makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { DialogProps, StyleClasses } from '@paintscout/ui'
import {
  DialogActions,
  DialogContent,
  Button,
  Typography,
  useDialogs,
  Grid,
  useClientOptions,
  useUser,
  Hidden,
  DialogTitle,
  IconButton,
  useIsMobile
} from '@paintscout/ui'
import ClearIcon from '@material-ui/icons/Clear'
import CheckIcon from '@material-ui/icons/Done'
import { getObjectLabels } from '@paintscout/util/builder'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { Sentry } from 'src/util/appSentry'
import { useMutation } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import { useHistory } from 'react-router-dom'
import { getHighestRole, triggerFlow } from '@paintscout/util'
import { useSearchUsersQuery } from '@paintscout/api'
import LogRocket from 'logrocket'

const useStyles = makeStyles<
  Theme,
  DialogProps & { showEarlyBirdBanner?: boolean; isPromptPage?: boolean; isTrialExpired?: boolean }
>(
  (theme) => ({
    dialogRoot: {
      zIndex: ({ isPromptPage }) => (isPromptPage ? '1 !important' : undefined)
    },
    dialogPaper: {
      overflowX: 'hidden',
      border: 'none',
      [theme.breakpoints.down('xs')]: {
        paddingTop: ({ isPromptPage }) => (isPromptPage ? theme.spacing(6) : 0),
        overflowY: 'auto'
      }
    },
    dialogTitle: {
      padding: theme.spacing(2, 3),
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.down('xs')]: {
        marginTop: ({ isPromptPage }) => (isPromptPage ? theme.spacing(6) : 0)
      }
    },
    title: {
      fontSize: '1.6rem'
    },
    mobileTitle: {
      fontSize: '1.5rem',
      marginBottom: theme.spacing(1),
      marginTop: ({ isTrialExpired, isPromptPage }) => (isTrialExpired && isPromptPage ? theme.spacing(5) : 0)
    },
    dialogContent: {
      padding: theme.spacing(4),
      [theme.breakpoints.down('xs')]: {
        maxHeight: ({ showEarlyBirdBanner }) => `calc(100vh - 70px - 48px - ${showEarlyBirdBanner ? 40 : 0}px)`,
        overflowY: 'auto'
      }
    },
    dialogContentBreakdown: {
      padding: theme.spacing(3)
    },
    closeIcon: {
      cursor: 'pointer',
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(2),
      padding: 0,
      zIndex: 3000,
      [theme.breakpoints.down('xs')]: {
        display: ({ showEarlyBirdBanner }) => (showEarlyBirdBanner ? 'none' : 'block'),
        top: theme.spacing(0.75),
        color: theme.palette.text.primary
      }
    },
    primaryColor: {
      color: theme.paintscout.palette.primary
    },
    mainContent: {
      marginTop: theme.spacing(2)
    },
    listItem: {
      padding: 0,
      paddingBottom: theme.spacing(0.5),
      display: 'flex',
      alignItems: 'flex-start'
    },
    listItemIcon: {
      minWidth: '16px',
      maxWidth: '16px',
      marginTop: theme.spacing(1)
    },
    checkIcon: {
      color: theme.paintscout.palette.primary,
      fontSize: theme.typography.pxToRem(16)
    },
    listItemText: {
      marginLeft: theme.spacing(1),
      whiteSpace: 'nowrap'
    },
    earlyBirdBanner: {
      position: 'absolute',
      top: 70,
      right: -70,
      padding: theme.spacing(0.75, 8),
      backgroundColor: theme.paintscout.palette.brand.Neutral.P35,
      color: theme.palette.common.white,
      transform: 'rotate(45deg)',
      textTransform: 'uppercase',
      fontSize: '1rem',
      zIndex: 2000,
      [theme.breakpoints.down('xs')]: {
        position: 'static',
        transform: 'none',
        textAlign: ({ isPromptPage }) => (isPromptPage ? 'center' : 'left'),
        padding: theme.spacing(1, 4),
        borderRadius: 0,
        margin: 0,
        marginTop: ({ isPromptPage }) => (isPromptPage ? theme.spacing(6.3) : 0)
      }
    },
    responsiveBreak: {
      display: 'none',
      [theme.breakpoints.down(440)]: {
        display: 'inline'
      }
    }
  }),
  { name: 'Dialog' }
)

export interface CRMPromptDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  isPromptPage?: boolean
  onConfirm?: () => any
  onCancel?: () => any
}

export default function CRMPromptDialog(props: CRMPromptDialogProps) {
  const showEarlyBirdBanner = moment().isBefore(moment('2026-01-01'))
  const classes = useStyles({ ...props, showEarlyBirdBanner })
  const { onCancel } = props
  const { dismissDialog } = useDialogs()
  const mobile = useIsMobile({ xs: true })

  const handleCancel = () => {
    dismissDialog()
    onCancel?.()
  }

  return (
    <Dialog {...props} classes={{ root: classes.dialogRoot, paper: classes.dialogPaper }} fullScreen={mobile}>
      <CRMPrompt {...props} handleCancel={handleCancel} />
    </Dialog>
  )
}

export function CRMPrompt(props: CRMPromptDialogProps & { handleCancel: () => void }) {
  const showEarlyBirdBanner = moment().isBefore(moment('2026-01-01'))
  const { isAdmin, isSubscribed, isTrial, trialExpired, user, isSuperadmin } = useUser()
  const classes = useStyles({ ...props, showEarlyBirdBanner, isTrialExpired: isTrial && trialExpired })
  const { onConfirm, isPromptPage, handleCancel } = props
  const [isLoading, setIsLoading] = useState(false)
  const { options, clientId: companyId, optionsSync, rates } = useClientOptions()

  const { enqueueSnackbar } = useSnackbar()
  const { quote: quoteLabel } = getObjectLabels({ options })
  const { dismissDialog, dismissAllDialogs } = useDialogs()
  const history = useHistory()
  const mobile = useIsMobile({ xs: true })

  const [content, setContent] = useState<'main' | 'price-breakdown'>('main')
  const [imgLoaded, setImgLoaded] = useState(false)

  const crmTrialExpiration = options.options?.crm?.trialExpiration
  const showSignupVariant =
    (!!crmTrialExpiration && moment(crmTrialExpiration).isBefore(moment())) ||
    !isSubscribed ||
    (isTrial && trialExpired)

  const isAnnual = user?.app_metadata?.company?.subscription?.billing_period_unit === 'year'
  const addonPrice = isAnnual ? (showEarlyBirdBanner ? 33 : 42) : showEarlyBirdBanner ? 39 : 49

  const { data: usersData } = useSearchUsersQuery({
    variables: {
      query: isSuperadmin
        ? `app_metadata.companyId:"${companyId}" AND app_metadata.active:true`
        : 'app_metadata.active:true',
      includeInactive: true
    },
    skip: !isAdmin,
    fetchPolicy: 'cache-and-network'
  })

  const users = usersData?.searchUsers?.rows ?? []
  const hasLegacyViewerUsers = users.some((user) => getHighestRole(user?.app_metadata?.roles || []) === 'viewer')

  const [turnOnCRM] = useMutation(gql`
    mutation TurnOnCRM($trial: Boolean!) {
      turnOnCRM(trial: $trial) {
        success
        trialExpiration
        crmAlreadyActive
        error
      }
    }
  `)

  const handleStartTrial = async () => {
    setIsLoading(true)
    try {
      // Call the GraphQL endpoint to turn on CRM features
      const { data } = await turnOnCRM({ variables: { trial: true } })
      if (!data.turnOnCRM.success) {
        console.log(data.turnOnCRM.error)
        Sentry.captureException(data.turnOnCRM.error, { tags: { companyId } })
        enqueueSnackbar('Failed to start CRM trial. Please try again later.', { variant: 'error' })
        setIsLoading(false)
        return
      }
      enqueueSnackbar('CRM trial started successfully!', { variant: 'success' })
      if (typeof BroadcastChannel != 'undefined') {
        const channel = new BroadcastChannel('subscription-updates')
        channel.postMessage('addon-modified')
        channel.postMessage('crm-trial-started')
      }
      await optionsSync({ optionsRev: options._rev, ratesRev: rates?._rev })

      LogRocket.track('CRMTrialStarted')
      if (onConfirm) {
        onConfirm()
      }
      dismissAllDialogs()
      triggerFlow('85160fab-9bea-4df6-8220-8bc23b77b835')
    } catch (error) {
      console.log(error)
      Sentry.captureException(error, { tags: { companyId } })
      enqueueSnackbar('Failed to start CRM trial. Please try again later.', { variant: 'error' })
      setIsLoading(false)
      dismissDialog()
    }
  }

  const MainContent = () => {
    const handleNext = () => {
      setContent('price-breakdown')
    }

    const handleSignup = () => {
      history.push('/settings/billing?section=billing-plan')
    }

    const handleLearnMore = () => {
      window.open(
        'https://help.paintscout.com/en/articles/11724113-paintscout-crm-guide',
        '_blank',
        'noopener,noreferrer'
      )
    }

    const getTitle = () => {
      if (showSignupVariant) {
        return <span className={classes.primaryColor}>Sign up for PaintScout CRM</span>
      }
      return (
        <>
          <span className={classes.primaryColor}>Try PaintScout CRM, </span>
          <span className={classes.responsiveBreak}>
            <br />
          </span>
          cancel anytime.
        </>
      )
    }

    const getSubTitle = () => {
      if (showSignupVariant) {
        return <>Only +${addonPrice} user/mo</>
      }
      return <>7-day free trial, then +{addonPrice} user/mo.</>
    }

    const getMarketingHeader = () => {
      return 'Run your business like a pro:'
    }

    const getMarketingPoints = () => {
      return [
        'Never let a deal slip through the cracks',
        `Hassle-free ${quoteLabel.value.toLowerCase()}  scheduling in seconds`,
        'Focus on selling, not copy-pasting',
        'Every detail that matters, in one place',
        'Turn clutter into clarity'
      ]
    }

    const getBottomContent = () => {
      if (!isAdmin && !showSignupVariant) {
        return 'To start your trial, please contact an admin on your team.'
      }
      if (!isAdmin && showSignupVariant) {
        return 'To continue access, please contact an admin on your team.'
      }
      return null
    }
    const bottomContent = getBottomContent()

    return (
      <>
        {showEarlyBirdBanner && (
          <Typography className={classes.earlyBirdBanner} variant="h3">
            EARLY BIRD PRICING
            <Hidden smUp>
              <IconButton
                size="small"
                onClick={handleCancel}
                style={{ padding: 0, color: 'white', position: 'absolute', right: 16, top: 7 }}
              >
                <ClearIcon />
              </IconButton>
            </Hidden>
          </Typography>
        )}
        <DialogContent classes={{ root: classes.dialogContent }}>
          {!isPromptPage && (
            <IconButton size="small" onClick={handleCancel} className={classes.closeIcon}>
              <ClearIcon />
            </IconButton>
          )}
          <Typography variant="h2" weight={700} className={mobile ? classes.mobileTitle : classes.title}>
            {getTitle()}
          </Typography>
          <Typography variant="h4" weight={700}>
            {getSubTitle()}
          </Typography>
          <Grid container className={classes.mainContent} spacing={3} alignItems="center">
            {bottomContent && (
              <Hidden smUp>
                <Grid item xs={12}>
                  <Typography color="textSecondary">{bottomContent}</Typography>
                </Grid>
              </Hidden>
            )}
            <Grid item xs={12} sm={6}>
              {!imgLoaded && (
                <Skeleton variant="rect" width={mobile ? '100%' : 350} height={mobile ? 430 : 230} animation="wave" />
              )}
              <img
                src={
                  mobile
                    ? 'https://paintscout.s3.us-east-1.amazonaws.com/billing-feats.png'
                    : 'https://paintscout.s3.us-east-1.amazonaws.com/product-marketing/crm-dialog-image.png'
                }
                alt="CRM Preview"
                width={'100%'}
                style={{
                  display: imgLoaded ? 'block' : 'none',
                  objectFit: 'cover'
                }}
                onLoad={() => setImgLoaded(true)}
              />
            </Grid>
            <Hidden xsDown>
              <Grid item xs={12} sm={6}>
                <Typography>
                  <strong>{getMarketingHeader()}</strong>
                  <List>
                    {getMarketingPoints().map((text) => (
                      <ListItem key={text} disableGutters className={classes.listItem}>
                        <ListItemIcon className={classes.listItemIcon}>
                          <CheckIcon color="primary" className={classes.checkIcon} />
                        </ListItemIcon>
                        <ListItemText primary={text} className={classes.listItemText} />
                      </ListItem>
                    ))}
                  </List>
                </Typography>
              </Grid>
            </Hidden>
            {bottomContent && (
              <Hidden xsDown>
                <Grid item xs={12}>
                  <Typography color="textSecondary">{bottomContent}</Typography>
                </Grid>
              </Hidden>
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          leftButton={
            isAdmin && (
              <Button variant={'text'} onClick={handleLearnMore}>
                Learn More
              </Button>
            )
          }
        >
          {isAdmin ? (
            <Button onClick={showSignupVariant ? handleSignup : handleNext} disabled={isLoading} loading={isLoading}>
              {showSignupVariant ? 'Sign Up' : 'Next'}
            </Button>
          ) : (
            <Button onClick={handleLearnMore}>Learn More</Button>
          )}
        </DialogActions>
      </>
    )
  }
  if (content === 'main') {
    return <MainContent />
  }

  const getPriceBreakdownContent = () => {
    if (hasLegacyViewerUsers) {
      return (
        <>
          <Typography>
            When your trial ends on{' '}
            <strong>
              {(crmTrialExpiration ? moment(crmTrialExpiration) : moment().add(7, 'days')).format('MMMM Do, YYYY')}
            </strong>
            , the CRM add-on will be applied to your account for each active user at a prorated amount for the remainder
            of your current billing period.
          </Typography>
          <Typography style={{ marginTop: 16 }}>
            <strong>{showEarlyBirdBanner ? 'Early Bird Pricing' : 'Price Breakdown'}</strong>
            <ul>
              <li>
                Sales/Admin:{' '}
                {`${showEarlyBirdBanner ? `an additional $${addonPrice}/user/month, then regular pricing of +$${isAnnual ? 42 : 49} user/month.` : `an additional $${addonPrice} user/month`}`}
              </li>
              <li>
                Office (view-only): a total of ${addonPrice}/user/month
                {showEarlyBirdBanner ? `, then regular pricing of $${isAnnual ? 42 : 49} user/month.` : '.'}
              </li>
            </ul>
          </Typography>
          {showEarlyBirdBanner && (
            <Typography style={{ marginTop: 24, marginBottom: 8 }}>
              Offer is available through December 31st, 2025. After that, regular pricing will apply.
            </Typography>
          )}
        </>
      )
    }
    return (
      <Typography>
        When your trial ends on{' '}
        <strong>
          {(crmTrialExpiration ? moment(crmTrialExpiration) : moment().add(7, 'days')).format('MMMM Do, YYYY')}
        </strong>
        , the CRM add-on will be added to your account at a prorated amount for the remainder of your current billing
        period.{' '}
        {showEarlyBirdBanner ? (
          <div style={{ marginTop: 16 }}>
            Early Bird pricing is available through December 31st, 2025. After that, regular pricing of +$
            {isAnnual ? 42 : 49}/user/month will resume.
          </div>
        ) : null}
      </Typography>
    )
  }
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DialogTitle
          classes={{ root: classes.dialogTitle }}
          rightContent={
            !isPromptPage ? (
              <IconButton size="small" style={{ padding: 0 }} onClick={handleCancel}>
                <ClearIcon />
              </IconButton>
            ) : null
          }
        >
          Price Breakdown
        </DialogTitle>
        <DialogContent classes={{ root: classes.dialogContentBreakdown }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {getPriceBreakdownContent()}
            </Grid>
            <Grid item xs={12}>
              <Typography>
                All changes are subject to{' '}
                <Link href="https://www.paintscout.com/terms-of-service/" target="_blank" rel="noopener noreferrer">
                  subscriber terms
                </Link>
                .
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          leftButton={
            <Button variant="textNoPadding" onClick={() => setContent('main')}>
              Back
            </Button>
          }
        >
          <Button onClick={handleStartTrial} disabled={isLoading} loading={isLoading}>
            Start Trial
          </Button>
        </DialogActions>
      </div>
    </>
  )
}
